import React, { useEffect, useState, useCallback, useRef } from "react";
import { Container, Graphics } from "@pixi/react";
import { useSelector } from "react-redux";
import anime from "animejs";
import {
  selectMenuActive,
  selectLeaveGamePopupActive,
} from "../../../../State/selectors";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import MenuToolbarTop from "./MenuToolbarTop";
import MenuToolbarBottom from "./MenuToolbarBottom";

// Overlay settings (matching WarningPopup pattern)
const OVERLAY_BACKGROUND_COLOR = 0x090f41;
const OVERLAY_BACKGROUND_ALPHA = 0.95;
const OVERLAY_FADE_DURATION = 200;
const CONTENT_ANIM_DURATION = 250; // Must match section fade-out duration

const MenuOverlay = () => {
  const menuActive = useSelector(selectMenuActive);
  const leaveGamePopupActive = useSelector(selectLeaveGamePopupActive);
  const { dimensions } = useDimensions();

  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const overlayAnimRef = useRef(null);
  const closeTimerRef = useRef(null);
  const overlayAlphaRef = useRef(0);

  // Keep alpha ref in sync
  useEffect(() => {
    overlayAlphaRef.current = overlayAlpha;
  }, [overlayAlpha]);

  // Open animation
  useEffect(() => {
    if (menuActive && !isClosing) {
      // Cancel any pending close
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }

      setIsVisible(true);

      if (overlayAnimRef.current) {
        overlayAnimRef.current.pause();
      }

      overlayAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
      });
    }
  }, [menuActive, isClosing]);

  // Close animation — triggered when menuActive goes false
  // Delay to let content sections fade out first (250ms), then overlay fades (200ms)
  useEffect(() => {
    if (!menuActive && isVisible && !isClosing) {
      setIsClosing(true);

      // Wait for section content to fade out before starting overlay fade
      closeTimerRef.current = setTimeout(() => {
        if (overlayAnimRef.current) {
          overlayAnimRef.current.pause();
        }

        overlayAnimRef.current = anime({
          targets: { alpha: overlayAlphaRef.current },
          alpha: 0,
          duration: OVERLAY_FADE_DURATION,
          easing: "easeInQuad",
          update: (anim) => {
            setOverlayAlpha(anim.animations[0].currentValue);
          },
          complete: () => {
            setOverlayAlpha(0);
            setIsVisible(false);
            setIsClosing(false);
            closeTimerRef.current = null;
          },
        });
      }, CONTENT_ANIM_DURATION);
    }
  }, [menuActive, isVisible, isClosing]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (overlayAnimRef.current) {
        overlayAnimRef.current.pause();
      }
    };
  }, []);

  // Draw fullscreen overlay background
  const drawOverlay = useCallback(
    (g) => {
      g.clear();
      g.beginFill(OVERLAY_BACKGROUND_COLOR);
      g.drawRect(0, 0, dimensions.width, dimensions.height);
      g.endFill();
    },
    [dimensions.width, dimensions.height],
  );

  if (!isVisible && !menuActive) {
    return null;
  }

  return (
    <Container name="menu_overlay" zIndex={9000} sortableChildren>
      {/* Fullscreen overlay background */}
      <Graphics
        draw={drawOverlay}
        alpha={overlayAlpha}
        eventMode="static"
        pointerdown={(e) => e.stopPropagation()}
        zIndex={9000}
      />

      {/* Menu toolbars */}
      <Container
        name="menu_toolbars"
        zIndex={9001}
        alpha={overlayAlpha > 0.01 && !leaveGamePopupActive ? 1 : 0}
      >
        <MenuToolbarTop />
        <MenuToolbarBottom />
      </Container>
    </Container>
  );
};

export default MenuOverlay;
